import i18n from '@/core/i18n';
import { BaseStatus, StatusConfig } from '@/modules/common';
import { createStatusOptionsMapper } from '@/modules/common/mappers/status-options.mapper';
import { MemberStatus } from '@/modules/team/types/team-member';

/**
 * Team member status classes that implement the BaseStatus interface.
 * Each status knows how to configure itself for the StatusBadge component.
 * Based on the design system specifications from Figma.
 */
export class TeamMemberStatusActive implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-green-600 text-green-600',
    };
  }
}

export class TeamMemberStatusInvited implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-blue-600 text-blue-600',
    };
  }
}

export class TeamMemberStatusInviteExpired implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-amber-600 text-amber-600',
    };
  }
}

export class TeamMemberStatusBasic implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-yellow-600 text-yellow-600',
    };
  }
}

export class TeamMemberStatusArchived implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-gray-500 text-gray-500',
    };
  }
}

export class TeamMemberStatusPlaceholder implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-violet-600 text-violet-600',
    };
  }
}

export class TeamMemberStatusNeedsReview implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-warning text-warning',
    };
  }
}

export class TeamMemberStatusUnknown implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-mutted-foreground text-mutted-foreground',
    };
  }
}

// Factory function to create status instances based on enum values
export const createTeamMemberStatus = (status: string): BaseStatus => {
  switch (status) {
    case MemberStatus.Active:
      return new TeamMemberStatusActive();
    case MemberStatus.Invited:
      return new TeamMemberStatusInvited();
    case MemberStatus.Expired:
      return new TeamMemberStatusInviteExpired();
    case MemberStatus.Basic:
      return new TeamMemberStatusBasic();
    case MemberStatus.Archived:
      return new TeamMemberStatusArchived();
    case MemberStatus.Placeholder:
      return new TeamMemberStatusPlaceholder();
    case MemberStatus.NeedsReview:
      return new TeamMemberStatusNeedsReview();
    default:
      return new TeamMemberStatusUnknown(); // Default fallback
  }
};

/**
 * Get localized status text for team member status.
 */
export const getMemberStatusText = (status: MemberStatus): string => {
  switch (status) {
    case MemberStatus.Active:
      return i18n.t('common:teamMemberStatus.active');
    case MemberStatus.Invited:
      return i18n.t('common:teamMemberStatus.invited');
    case MemberStatus.Basic:
      return i18n.t('common:teamMemberStatus.basic');
    case MemberStatus.Expired:
      return i18n.t('common:teamMemberStatus.expired');
    case MemberStatus.Archived:
      return i18n.t('common:teamMemberStatus.archived');
    case MemberStatus.Placeholder:
      return i18n.t('common:teamMemberStatus.placeholder');
    case MemberStatus.NeedsReview:
      return i18n.t('common:teamMemberStatus.needsReview');
    default:
      return i18n.t('common:teamMemberStatus.unknown');
  }
};

export const getMemberStatusColor = (status: MemberStatus): string => {
  switch (status) {
    case MemberStatus.Active:
      return 'green-600';
    case MemberStatus.Invited:
      return 'blue-600';
    case MemberStatus.Basic:
      return 'yellow-600';
    case MemberStatus.Expired:
      return 'amber-600';
    case MemberStatus.Archived:
      return 'gray-500';
    case MemberStatus.Placeholder:
      return 'violet-600';
    case MemberStatus.NeedsReview:
      return 'warning';
    default:
      return 'muted-foreground';
  }
};

/**
 * Preconfigured mapper for team member status options.
 * Maps an array of MemberStatus values to option objects with label, value, and color.
 */
export const getTeamMemberStatusOptions = createStatusOptionsMapper<MemberStatus>({
  getStatusColor: getMemberStatusColor,
  getStatusText: getMemberStatusText,
});
